/**
 * PolicySideSheet — slide-in panel showing the exact Vault / Ledger /
 * Pinned company-doc policy paragraph that triggered an [L#] / [P#]
 * citation. Visual pattern mirrors `SourcesSideSheet` (statutory side
 * panel) so the two read as siblings — same width, same dismiss
 * behaviour, same RTL handling, but tinted with the policy-blue accent
 * (#2563EB) to signal "this is from your own policy library, not Saudi
 * statute".
 *
 * Activation: click any `[L#]` or `[P#]` pill rendered by
 * renderWithCitations() in AgentPreviewPage.jsx.
 *
 * Data shape (from backend `envelope.result.output.policy_docs`):
 *   {
 *     P: [{ kind, index, marker, filename, category, document_type,
 *          document_id, snippet, similarity }, …],
 *     L: [{ kind, index, marker, filename, article_label,
 *          document_id, snippet, similarity }, …],
 *   }
 *
 * `highlightedMarker` ("P1", "L2", …) selects which doc to focus on.
 */
import React, { useEffect, useMemo } from 'react';

const escClose = (onClose) => (e) => {
  if (e.key === 'Escape') onClose?.();
};

export function PolicySideSheet({
  isOpen,
  onClose,
  policyDocs = { P: [], L: [] },
  isArabic = true,
  highlightedMarker = null,
}) {
  // Esc-to-close — same pattern as SourcesSideSheet.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = escClose(onClose);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Resolve which entry should appear FIRST (the one the user clicked).
  // Other entries are listed below for context.
  const orderedEntries = useMemo(() => {
    const p = Array.isArray(policyDocs?.P) ? policyDocs.P : [];
    const l = Array.isArray(policyDocs?.L) ? policyDocs.L : [];
    const all = [...p, ...l];
    if (!highlightedMarker) return all;
    const idx = all.findIndex((e) => `${e.kind}${e.index}` === highlightedMarker);
    if (idx <= 0) return all;
    return [all[idx], ...all.slice(0, idx), ...all.slice(idx + 1)];
  }, [policyDocs, highlightedMarker]);

  if (!isOpen) return null;

  return (
    <>
      {/* Click-outside dimmer (transparent — same pattern as the statutory sheet) */}
      <button
        type="button"
        aria-label={isArabic ? 'إغلاق نص السياسة' : 'Close policy text'}
        onClick={onClose}
        data-testid="policy-side-sheet-overlay"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'transparent',
          border: 0,
          padding: 0,
          margin: 0,
          zIndex: 49,
          cursor: 'default',
        }}
      />
      <aside
        role="dialog"
        aria-modal="false"
        aria-label={isArabic ? 'عرض نص السياسة' : 'View policy text'}
        dir={isArabic ? 'rtl' : 'ltr'}
        className="font-arabic"
        data-testid="policy-side-sheet"
        style={{
          position: 'fixed',
          [isArabic ? 'left' : 'right']: 0,
          top: '48px',
          width: '360px',
          height: 'calc(100vh - 48px)',
          background: '#FFFFFF',
          borderInlineStart: isArabic ? '1px solid #E8E8E8' : 'none',
          borderInlineEnd: isArabic ? 'none' : '1px solid #E8E8E8',
          zIndex: 50,
          overflowY: 'auto',
          padding: '24px 20px',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}>
          <div>
            <h3 style={{
              fontSize: '15px',
              fontWeight: 500,
              color: '#111111',
              margin: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '9999px',
                background: '#2563EB',
              }} aria-hidden="true" />
              {isArabic ? 'عرض نص السياسة' : 'View policy text'}
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#999999',
              marginTop: '4px',
              marginBottom: 0,
              lineHeight: 1.5,
            }}>
              {isArabic
                ? 'الفقرة المُسترجَعة من خزانتك القانونية التي شغّلت هذا الاستشهاد'
                : 'The exact paragraph from your Library that triggered this citation'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={isArabic ? 'إغلاق' : 'Close'}
            data-testid="policy-side-sheet-close"
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#999999',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {orderedEntries.length === 0 ? (
          <div style={{ fontSize: '12px', color: '#999999' }}>
            {isArabic
              ? 'لا توجد فقرات سياسة مرفقة بهذه الإجابة.'
              : 'No policy paragraphs attached to this answer.'}
          </div>
        ) : (
          orderedEntries.map((entry, i) => {
            const marker = `${entry.kind}${entry.index}`;
            const isFocused = marker === highlightedMarker;
            const sim = (typeof entry.similarity === 'number' && entry.similarity > 0)
              ? `${Math.round(entry.similarity * 100)}%`
              : null;
            const isPinned = entry.kind === 'P';
            const tagLabel = isPinned
              ? (isArabic ? 'مثبت دائماً' : 'always pinned')
              : (isArabic ? 'مطابقة دلالية' : 'semantic match');
            const filename = entry.filename || (isArabic ? 'مستند' : 'Document');
            const article = entry.article_label || null;
            const snippet = (entry.snippet || '').toString();

            return (
              <div
                key={`${marker}-${i}`}
                id={`policy-card-${marker}`}
                data-testid={`policy-side-sheet-card-${marker}`}
                style={{
                  background: isFocused ? 'rgba(37, 99, 235, 0.04)' : '#F7F7F5',
                  border: isFocused
                    ? '1px solid rgba(37, 99, 235, 0.40)'
                    : '1px solid #E8E8E8',
                  borderRadius: '8px',
                  padding: '14px',
                  marginBottom: '10px',
                  transition: 'border-color 0.2s, background-color 0.2s',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '6px',
                }}>
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '20px',
                      height: '16px',
                      padding: '0 5px',
                      background: '#2563EB',
                      color: '#FFFFFF',
                      borderRadius: '9999px',
                      fontSize: '9px',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    }}
                  >
                    {marker}
                  </span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#111111',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={filename}
                  >
                    {filename}
                  </span>
                </div>

                {(article || isPinned) && (
                  <div style={{
                    fontSize: '11px',
                    color: '#666666',
                    marginBottom: '8px',
                  }}>
                    {article || (isArabic ? 'وثيقة سياسة كاملة' : 'full policy document')}
                  </div>
                )}

                <div
                  style={{
                    fontSize: '12px',
                    color: '#444444',
                    lineHeight: 1.7,
                    direction: isArabic ? 'rtl' : 'ltr',
                    textAlign: isArabic ? 'right' : 'left',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  &ldquo;{snippet.length > 600 ? `${snippet.slice(0, 600)}…` : snippet}&rdquo;
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '10px',
                  fontSize: '10px',
                  color: '#BBBBBB',
                }}>
                  <span>{tagLabel}</span>
                  {sim && (
                    <span data-testid={`policy-side-sheet-sim-${marker}`}>
                      {isArabic ? 'تطابق' : 'match'}: {sim}
                    </span>
                  )}
                </div>

                {entry.document_id && (
                  <div style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #EFEFEF',
                    textAlign: isArabic ? 'left' : 'right',
                  }}>
                    <a
                      href={`/v2/vault?doc=${encodeURIComponent(entry.document_id)}${
                        entry.snippet ? `&snippet=${encodeURIComponent(String(entry.snippet).slice(0, 200))}` : ''
                      }`}
                      data-testid={`policy-side-sheet-open-vault-${marker}`}
                      style={{
                        fontSize: '11px',
                        color: '#666666',
                        textDecoration: 'underline',
                        textDecorationColor: '#CCCCCC',
                        textUnderlineOffset: '3px',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#2563EB';
                        e.currentTarget.style.textDecorationColor = '#2563EB';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#666666';
                        e.currentTarget.style.textDecorationColor = '#CCCCCC';
                      }}
                    >
                      {isArabic ? 'فتح في الخزانة' : 'Open in Vault'}
                      <span aria-hidden="true" style={{ marginInlineStart: '4px' }}>
                        {isArabic ? '←' : '→'}
                      </span>
                    </a>
                  </div>
                )}
              </div>
            );
          })
        )}
      </aside>
    </>
  );
}

export default PolicySideSheet;
